import classNames from 'classnames/bind';
import { Button } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper';
import { motion, Variants } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import useWindowSize from 'hooks/useWindowSize';
import styles from './PartnershipSection.module.css';
import { useRouter } from 'next/router';
import { PARTNERSHIP_SLIDE_COUNT } from 'constants/partnership';
import { getAssetLocale, getAssetSuffix } from 'utils/locale';

const cx = classNames.bind(styles);

type PartnershipSlideProps = {
  index: number;
  totalSlideCnt: number;
  locale: string | undefined;
};

const cardVariants: Variants = {
  offscreen: {
    opacity: 0,
  },
  onscreen: {
    opacity: 1,
    transition: {
      type: 'spring',
      bounce: 0.1,
      duration: 1,
    },
  },
};

const PartnershipSlide = ({
  index,
  totalSlideCnt,
  locale,
}: PartnershipSlideProps) => {
  return (
    <div
      className={cx(
        `swiper-image-${String(index + 1).padStart(2, '0')}${getAssetSuffix(
          locale
        )}`
      )}
    >
      <Button
        type="primary"
        shape="round"
        size="small"
        className={cx('swiper-button')}
      >
        <span>{index + 1}</span>/{totalSlideCnt}
      </Button>
    </div>
  );
};

function PartnershipSection() {
  const { t } = useTranslation('business');
  const router = useRouter();
  const { locale } = router;

  const { width } = useWindowSize();
  const isMobile = width ? width <= 780 : false;
  const totalSlideCnt = PARTNERSHIP_SLIDE_COUNT[getAssetLocale(locale)];

  return (
    <motion.section
      initial="offscreen"
      whileInView="onscreen"
      viewport={{ once: true }}
      id="partnership"
    >
      <motion.div
        variants={cardVariants}
        className={cx('partnership-container', 'dark-blue-wrapper')}
      >
        <section className={cx('content-wrapper')}>
          <h1>{t('partnership')}</h1>
          <h2>
            <Trans
              i18nKey={t('partnership-description')}
              components={{
                br: <br className="disable-mobile-break" />,
              }}
            />
          </h2>
        </section>
      </motion.div>
      <motion.div variants={cardVariants} className={cx('swiper-wrapper')}>
        <Swiper
          grabCursor={true}
          slidesPerView={isMobile ? 1 : 1.75}
          centeredSlides={true}
          spaceBetween={isMobile ? 0 : 200}
          slidesPerGroup={1}
          initialSlide={0}
          modules={[Navigation]}
          navigation={true}
          className={cx('partnership-swiper')}
        >
          {Array.from({ length: totalSlideCnt }, (_, i) => (
            <SwiperSlide key={i + 1}>
              <PartnershipSlide
                index={i}
                totalSlideCnt={totalSlideCnt}
                locale={locale}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </motion.div>
      <motion.div
        variants={cardVariants}
        className={cx('partnership-container', 'pastel-blue-wrapper')}
      >
        <section className={cx('content-wrapper')}>
          <h1>
            {t('branded-content')} / {t('integrated-marketing')}
          </h1>
          <h2>{t('branded-content')}</h2>
          <p>
            <Trans
              i18nKey={t('branded-content-description')}
              components={{
                br: <br className="disable-mobile-break" />,
              }}
            />
          </p>
          <h2>{t('integrated-marketing')}</h2>
          <p>
            <Trans
              i18nKey={t('integrated-marketing-description')}
              components={{
                br: <br className="disable-mobile-break" />,
              }}
            />
          </p>
        </section>
      </motion.div>
    </motion.section>
  );
}

export default PartnershipSection;
